import Image from 'next/image'
import {FC} from 'react'

import {Link} from '@/i18n'
import {BLOG_THUMBNAIL_HEIGHT, BLOG_THUMBNAIL_WIDTH} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {BlogOverview} from '@/utils/contentful'
import {formatDate} from '@/utils/formatDate'
import {useLanguage} from '@/utils/i18n/language'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {Text, TextFontWeight, TextVariant} from '../common/content/Text'

type BlogPreviewProps = {
  blog: BlogOverview
  linkHref: string
}

export const BlogPreview: FC<React.PropsWithChildren<BlogPreviewProps>> = ({blog, linkHref}) => {
  const language = useLanguage()
  return (
    <>
      <article className="article">
        <div className="wrapper">
          <Link href={linkHref}>
            <div className="image-wrapper">
              <Image
                src={blog.image.url}
                alt={blog.image.alt}
                style={{objectFit: 'cover'}}
                fill
                sizes={`${MOBILE_SCREENSIZE} 100vw, ${BLOG_THUMBNAIL_WIDTH}`}
              />
            </div>
          </Link>
          <div className="content">
            <div className="meta">
              <Text variant={TextVariant.text14} color={theme.palette.text.light}>
                <span className="meta-info">{formatDate(blog.date, language, true)}</span>
                <span className="meta-info">{blog.readingTime}</span>
              </Text>
            </div>
            <Text
              variant={TextVariant.h5}
              element="h3"
              className="underline"
              fontWeight={TextFontWeight.bold}
            >
              <Link href={linkHref} legacyBehavior>
                <a className="title">{blog.title}</a>
              </Link>
            </Text>
            <div className="intro">
              <Text>{blog.blogIntro}</Text>
            </div>
          </div>
        </div>
      </article>

      <style jsx>{`
        .article + .article {
          position: relative;
          padding-top: ${theme.spacing[5]};
          margin-top: ${theme.spacing[5]};
        }

        .article + .article:before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background-color: ${theme.palette.butter[100]};
        }

        .wrapper {
          display: flex;
          align-items: center;
        }

        .image-wrapper {
          position: relative;
          width: ${BLOG_THUMBNAIL_WIDTH};
          height: ${BLOG_THUMBNAIL_HEIGHT};
          flex: 0 0 ${BLOG_THUMBNAIL_WIDTH};
          background-color: ${theme.palette.skeletonLoading.main};
        }

        .content {
          margin-left: ${theme.spacing[5]};
        }

        .title {
          color: inherit;
        }

        .meta {
          margin-bottom: ${theme.spacing[3]};
        }

        .meta-info {
          position: relative;
        }

        .meta-info + .meta-info {
          padding-left: ${theme.spacing[3]};
          display: inline-flex;
          align-items: center;
        }

        .meta-info + .meta-info:before {
          content: '';
          position: absolute;
          left: 6px;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background-color: ${theme.palette.text.light};
        }

        .title,
        .intro {
          text-overflow: ellipsis;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
        .intro {
          margin-top: ${theme.spacing[3]};
        }

        @media ${MOBILE_SCREENSIZE} {
          .article + .article {
            padding-top: ${theme.spacing[4]};
            margin-top: ${theme.spacing[4]};
          }

          .wrapper {
            flex-direction: column;
            align-items: inherit;
          }

          .image-wrapper {
            width: 100%;
            flex: inherit;
          }

          .content {
            margin-left: 0;
            margin-top: ${theme.spacing[3]};
          }

          .meta {
            margin-bottom: ${theme.spacing[2]};
          }

          .intro {
            margin-top: ${theme.spacing[2]};
          }
        }
      `}</style>
    </>
  )
}
